import { createStore } from "vuex";
import Cookies from "js-cookie";
import {
  addTeam as apiAddTeam,
  autoSchedule as apiAutoSchedule,
  cancelRegistrations as apiCancelRegistrations,
  getRegistrationData as apiGetRegistrationData,
  getRegistrationInfo as apiGetRegistrationInfo,
  getTeamData as apiGetTeamData,
  login as apiLogin,
  logout as apiLogout,
  mailRegistrations as apiMailRegistrations,
  requestAllBlurbs as apiRequestAllBlurbs,
  requestAuthInfo as apiRequestAuthInfo,
  requestBlurb as apiRequestBlurb,
  requestGlobalConfig as apiRequestGlobalConfig,
  saveBlurb as apiSaveBlurb,
  setDateTime as apiSetDateTime,
  uncancelRegistrations as apiUncancelRegistrations,
  unscheduleRegistrations as apiUnscheduleRegistrations,
  updateTeam as apiUpdateTeam,
} from "@/api";

function isAuthError(error) {
  return error && (error.status === 401 || error.status === 403);
}

function applyTeamData(state, payload) {
  state.teams.all = payload.all;
  state.teams.defaultTeamId =
    payload.default_team_id !== null &&
    typeof payload.default_team_id !== "undefined"
      ? String(payload.default_team_id)
      : null;

  const hasSelectedTeam =
    state.teams.selectedTeam &&
    Object.prototype.hasOwnProperty.call(
      state.teams.all,
      state.teams.selectedTeam
    );
  if (!hasSelectedTeam) {
    state.teams.selectedTeam =
      state.teams.defaultTeamId ||
      Object.keys(state.teams.all || {})[0] ||
      null;
  }
}

function applyRegistrationData(state, payload) {
  state.registrations.registrations = payload.registrations || [];
  state.registrations.route = payload.route || [];
  state.registrations.hasFetchedRegistrations = true;
  if (payload.change_type) {
    state.filters.selectedRegistrationType = payload.change_type;
  }
}

function applyGlobalConfig(state, payload) {
  state.seasons = payload.seasons || {};
  state.defaultSeasonId = payload.default_season_id;
  if (!state.filters.selectedSeason && state.defaultSeasonId) {
    state.filters.selectedSeason = state.defaultSeasonId;
  }
}

// Helper function to retrieve blurb by name
function getBlurbByName(state, name) {
  const blurb = state.blurbs[name];
  if (typeof blurb !== "undefined") {
    return blurb;
  }

  if (!state.blurbRequests[name]) {
    store.dispatch("requestBlurb", name);
  }
  return "Loading...";
}

const store = createStore({
  state: {
    messages: [],
    auth: {
      is_auth: false,
      username: "",
    },
    teams: {
      selectedTeam: null,
      defaultTeamId: null,
      all: {
        1: {
          name: "",
          time_sheep: 0,
          time_alpaca: 0,
          time_llama: 0,
          time_setup: 0,
          time_setup_many_sheep: 0,
          many_sheep_thres: 0,
          time_teardown: 0,
          time_extra_driving: 0,
          begin_time: "07:00:00",
          max_day_time: 0,
        },
      },
    },
    filters: {
      fromDate: Cookies.get("fromDate") || null,
      toDate: Cookies.get("toDate") || null,
      selectedRegistrationType: "scheduled",
      seasonId: 1,
      selectedRegistrations: [],
      selectedSeason: null,
      searchStr: "",
    },
    blurbs: {},
    blurbRequests: {},
    registrations: {
      displayRegistration: {
        scheduled_start: "1970-01-01T00:00:00",
      },
      registrations: [],
      route: [],
      hasFetchedRegistrations: false,
    },
    seasons: {},
    defaultSeasonId: null,
  },
  getters: {
    getBlurbByName: (state) => (name) => getBlurbByName(state, name),
    registrationRequestData: (state) => ({
      type: state.filters.selectedRegistrationType,
      team: state.teams.selectedTeam || state.teams.defaultTeamId,
      season: state.filters.selectedSeason,
      from_date: state.filters.fromDate,
      to_date: state.filters.toDate,
    }),
  },
  mutations: {
    addMessage(state, message) {
      state.messages.push(message);
    },
    setBlurbTexts(state, blurbs) {
      state.blurbs = blurbs || {};
    },
    setBlurbRequestPending(state, { name, pending }) {
      if (pending) {
        state.blurbRequests = { ...state.blurbRequests, [name]: true };
      } else {
        const next = { ...state.blurbRequests };
        delete next[name];
        state.blurbRequests = next;
      }
    },
    setSelectedTeam(state, newValue) {
      state.teams.selectedTeam = newValue;
    },
    updateFilters(state, filters) {
      state.filters.fromDate = filters.fromDate;
      state.filters.toDate = filters.toDate;
      state.filters.selectedRegistrationType = filters.selectedRegistrationType;
      state.filters.selectedSeason = filters.selectedSeason;
      state.filters.selectedRegistrations = [];
    },
    updateSearchStr(state, searchStr) {
      state.filters.searchStr = searchStr;
    },
    updateSelectedRegistrations(state, registrations) {
      state.filters.selectedRegistrations = registrations;
    },
    setAuthState(state, { is_auth, username }) {
      state.auth.is_auth = !!is_auth;
      state.auth.username = username || "";
    },
    setTeamData(state, payload) {
      applyTeamData(state, payload);
    },
    setGlobalConfig(state, payload) {
      applyGlobalConfig(state, payload);
    },
    setRegistrationData(state, payload) {
      applyRegistrationData(state, payload);
    },
    setRegistrationInfo(state, registration) {
      state.registrations.displayRegistration = registration;
    },
  },
  actions: {
    async initializeApp({ dispatch }) {
      await Promise.all([
        dispatch("fetchAllBlurbs"),
        dispatch("fetchAuthInfo"),
        dispatch("fetchGlobalConfig"),
      ]);
    },
    async fetchAllBlurbs({ commit }) {
      try {
        const data = await apiRequestAllBlurbs();
        commit("setBlurbTexts", data.blurbs);
        return true;
      } catch (error) {
        console.error("Failed to fetch blurbs", error);
        return false;
      }
    },
    async fetchAuthInfo({ commit }) {
      try {
        const data = await apiRequestAuthInfo();
        commit("setAuthState", {
          is_auth: data.is_authenticated,
          username: data.username,
        });
        return true;
      } catch (error) {
        if (isAuthError(error)) {
          commit("setAuthState", { is_auth: false, username: "" });
          return false;
        }
        console.error("Failed to fetch auth info", error);
        return false;
      }
    },
    async fetchGlobalConfig({ commit }) {
      try {
        const data = await apiRequestGlobalConfig();
        commit("setGlobalConfig", data);
        return true;
      } catch (error) {
        console.error("Failed to fetch global config", error);
        return false;
      }
    },
    async cancelRegistrations({ state, getters, commit }) {
      const params = {
        ...getters.registrationRequestData,
        ids: state.filters.selectedRegistrations,
      };
      try {
        const data = await apiCancelRegistrations(params);
        commit("setRegistrationData", data);
        return true;
      } catch (error) {
        if (isAuthError(error)) {
          commit("setAuthState", { is_auth: false, username: "" });
          return false;
        }
        console.error("Failed to cancel registrations", error);
        return false;
      }
    },
    async fetchRegistrationInfo({ commit }, uuid) {
      try {
        const data = await apiGetRegistrationInfo(uuid);
        commit("setRegistrationInfo", data.registration);
        return true;
      } catch (error) {
        console.error("Failed to fetch registration info", error);
        return false;
      }
    },
    async fetchRegistrations({ getters, commit }) {
      const params = { ...getters.registrationRequestData };
      if (!params.season || !params.team) {
        return false;
      }

      try {
        const data = await apiGetRegistrationData(params);
        commit("setRegistrationData", data);
        return true;
      } catch (error) {
        if (isAuthError(error)) {
          commit("setAuthState", { is_auth: false, username: "" });
          return false;
        }
        console.error("Failed to fetch registrations", error);
        return false;
      }
    },
    async mailRegistrations(
      { state, getters, commit },
      { name, registration_id }
    ) {
      const params = {
        ...getters.registrationRequestData,
        name,
        ids:
          typeof registration_id !== "undefined"
            ? [registration_id]
            : state.filters.selectedRegistrations,
      };
      try {
        const data = await apiMailRegistrations(params);
        commit("setRegistrationData", data);
        return true;
      } catch (error) {
        if (isAuthError(error)) {
          commit("setAuthState", { is_auth: false, username: "" });
          return false;
        }
        console.error("Failed to send registration mail", error);
        return false;
      }
    },
    async setDateTime({ state, getters, commit }, { date, time }) {
      const params = {
        ...getters.registrationRequestData,
        ids: state.filters.selectedRegistrations,
        set_date: date,
        set_time: time,
      };
      try {
        const data = await apiSetDateTime(params);
        commit("setRegistrationData", data);
        return true;
      } catch (error) {
        if (isAuthError(error)) {
          commit("setAuthState", { is_auth: false, username: "" });
          return false;
        }
        console.error("Failed to set datetime", error);
        return false;
      }
    },
    async uncancelRegistrations({ state, getters, commit }) {
      const params = {
        ...getters.registrationRequestData,
        ids: state.filters.selectedRegistrations,
      };
      try {
        const data = await apiUncancelRegistrations(params);
        commit("setRegistrationData", data);
        return true;
      } catch (error) {
        if (isAuthError(error)) {
          commit("setAuthState", { is_auth: false, username: "" });
          return false;
        }
        console.error("Failed to uncancel registrations", error);
        return false;
      }
    },
    async unscheduleRegistrations({ state, getters, commit }) {
      const params = {
        ...getters.registrationRequestData,
        ids: state.filters.selectedRegistrations,
      };
      try {
        const data = await apiUnscheduleRegistrations(params);
        commit("setRegistrationData", data);
        return true;
      } catch (error) {
        if (isAuthError(error)) {
          commit("setAuthState", { is_auth: false, username: "" });
          return false;
        }
        console.error("Failed to unschedule registrations", error);
        return false;
      }
    },
    updateBlurbTexts({ commit }, blurbs) {
      commit("setBlurbTexts", blurbs);
    },
    async saveBlurbByName({ commit }, { name, value }) {
      try {
        const data = await apiSaveBlurb({ name, value });
        commit("setBlurbTexts", data.blurbs);
        return true;
      } catch (error) {
        if (isAuthError(error)) {
          commit("setAuthState", { is_auth: false, username: "" });
          return false;
        }
        console.error("Failed to save blurb", error);
        return false;
      }
    },
    async sendLogin({ commit, dispatch }, { username, password }) {
      try {
        const data = await apiLogin({ username, password });
        commit("setAuthState", {
          is_auth: data.is_authenticated,
          username: data.username,
        });
        await dispatch("fetchGlobalConfig");
        await dispatch("fetchAuthInfo");
        if (data.is_authenticated) {
          await dispatch("fetchTeamData");
        }
        return !!data.is_authenticated;
      } catch (error) {
        console.error("Login request failed", error);
        commit("setAuthState", { is_auth: false, username: "" });
        return false;
      }
    },
    async sendLogout({ commit }) {
      try {
        await apiLogout();
        commit("setAuthState", { is_auth: false, username: "" });
        return true;
      } catch (error) {
        if (isAuthError(error)) {
          commit("setAuthState", { is_auth: false, username: "" });
          return true;
        }
        console.error("Logout request failed", error);
        return false;
      }
    },
    async requestBlurb({ state, commit }, name) {
      if (!name || state.blurbRequests[name]) {
        return false;
      }

      commit("setBlurbRequestPending", { name, pending: true });
      try {
        const data = await apiRequestBlurb(name);
        commit("setBlurbTexts", data.blurbs);
        return true;
      } catch (error) {
        console.error(`Failed to fetch blurb ${name}`, error);
        return false;
      } finally {
        commit("setBlurbRequestPending", { name, pending: false });
      }
    },
    async fetchTeamData({ commit }) {
      try {
        const data = await apiGetTeamData();
        commit("setTeamData", data);
        return true;
      } catch (error) {
        if (isAuthError(error)) {
          commit("setAuthState", { is_auth: false, username: "" });
          return false;
        }
        console.error("Failed to fetch team data", error);
        return false;
      }
    },
    async sendTeamUpdate({ commit }, teamData) {
      try {
        const data = await apiUpdateTeam(teamData);
        commit("setTeamData", data);
        return true;
      } catch (error) {
        if (isAuthError(error)) {
          commit("setAuthState", { is_auth: false, username: "" });
          return false;
        }
        console.error("Failed to update team", error);
        return false;
      }
    },
    async addTeam({ commit }, name) {
      try {
        const data = await apiAddTeam(name);
        commit("setTeamData", data);
        return true;
      } catch (error) {
        if (isAuthError(error)) {
          commit("setAuthState", { is_auth: false, username: "" });
          return false;
        }
        console.error("Failed to add team", error);
        return false;
      }
    },
    async autoSchedule(
      { state, getters, commit },
      { fromDate, fromTime, includeScheduled, toDate }
    ) {
      const params = {
        ...getters.registrationRequestData,
        ids: state.filters.selectedRegistrations,
        fromDate,
        fromTime,
        includeScheduled,
        toDate,
      };
      try {
        const data = await apiAutoSchedule(params);
        commit("setRegistrationData", data);
        return true;
      } catch (error) {
        if (isAuthError(error)) {
          commit("setAuthState", { is_auth: false, username: "" });
          return false;
        }
        console.error("Failed to auto-schedule", error);
        return false;
      }
    },
  },
  modules: {},
});

export default store;
